import React from "react";
import PropTypes from "prop-types";
import DocumentAuthors from "@modules/Document/DocumentAuthors";
import { renderAuthorsBackup } from "../utils/documentUtils";

const STYLES = {
  contentArea: {
    display: "flex",
    marginTop: "16px",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
  },
  topSection: {
    display: "flex",
    marginBottom: "-2px",
    alignItems: "flex-start",
    marginTop: "15px",
  },
  leftColumn: {
    width: "80px",
    marginRight: "16px",
    display: "flex",
    flexDirection: "column",
    marginTop: "32px",
  },
  callNumberSection: {
    textAlign: "center",
    marginTop: "-20px",
  },
  callNumberValue: {
    fontSize: "17px",
    fontWeight: "bold",
    padding: "43px",
    minHeight: "60px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    marginTop: "-37px",
  },
  callNumberPrefix: {
    fontSize: "21px",
    fontWeight: "bod",
    color: "#000",
    marginBottom: "0px",
    letterSpacing: "1.5px",
    marginTop: "-42px",
  },
  callNumberMain: {
    fontSize: "21px",
    fontWeight: "bod",
    color: "#000",
    marginBottom: "13px",
    letterSpacing: "1.5px",
    marginTop: "10px",
    minHeight: "75px",
  },
  callNumberMainPlaceholder: {
    fontSize: "21px",
    fontWeight: "bod",
    color: "#000",
    marginBottom: "13px",
    letterSpacing: "1.5px",
    marginTop: "10px",
    minHeight: "75px",
    visibility: "hidden",
  },
  callNumberSuffix: {
    fontSize: "21px",
    fontWeight: "bold",
    color: "#000",
    marginTop: "-5px",
    letterSpacing: "1.5px",
    marginBottom: "-42px",
  },
  callNumberLabel: {
    fontSize: "12px",
    marginTop: "2px",
  },
  rightSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  accessionNumber: {
    textAlign: "right",
    marginBottom: "8px",
    position: "absolute",
    top: "105px",
    right: "16px",
    marginTop: "-15px",
  },
  accessionLabel: {
    fontSize: "10px",
    fontWeight: "normal",
    display: "none",
  },
  accessionValue: {
    fontSize: "21px",
    fontWeight: "bold",
  },
  titleSection: {
    marginBottom: "12px",
  },
  titleValue: {
    fontSize: "15px",
    borderBottom: "1px solid #333",
    padding: "4px 0",
    minHeight: "10px",
    textAlign: "center",
    marginBottom: "10px",
  },
  titleLabel: {
    fontSize: "13px",
    textAlign: "center",
    marginTop: "2px",
    fontWeight: "bold",
  },
  authorSection: {
    marginBottom: "12px",
  },
  authorValue: {
    fontSize: "15px",
    borderBottom: "1px solid #333",
    padding: "4px 0",
    textAlign: "center",
    marginBottom: "10px",
    minHeight: "16px",
  },
  authorLabel: {
    fontSize: "13px",
    textAlign: "center",
    marginTop: "2px",
    fontWeight: "bold",
  },
};

const CONSTANTS = {
  AUTHOR_LIMIT: 3,
};

// Helper function to format call number for display in the call number box
const formatCallNumberForDisplay = (callNumber) => {
  if (!callNumber || callNumber === "N/A") {
    return ["", "", ""];
  }

  // Split the call number into parts for display
  const parts = callNumber.split(" ");

  // Return up to 3 parts for the display box
  return [
    parts[0] || "",
    parts[1] || "",
    parts.slice(2).join(" ") || "", // Join remaining parts for third line
  ];
};

const BookCardField = ({
  title,
  authors,
  hasOtherAuthors,
  callNumber,
  callNumberPrefix,
  baseCallNumber,
  callNumberSuffix,
  barcode,
  formData,
  children,
}) => {
  const renderAuthors = () => {
    if (Array.isArray(authors) && authors.length > 0) {
      return (
        <DocumentAuthors
          authors={authors}
          hasOtherAuthors={hasOtherAuthors}
          prefix=""
          limit={CONSTANTS.AUTHOR_LIMIT}
        />
      );
    }
    return renderAuthorsBackup(authors, formData);
  };

  // Use the base call number for display in the box, with prefix above and suffix below
  const displayCallNumber = baseCallNumber || callNumber;
  const callNumberParts = formatCallNumberForDisplay(displayCallNumber);

  // Calculate dynamic font size based on call number length
  const getCallNumberFontSize = () => {
    const totalLength = callNumberParts.join("").length;

    if (totalLength <= 14) return "21px";
    if (totalLength <= 18) return "15px";
    if (totalLength <= 24) return "14px";
    if (totalLength <= 30) return "10px";
    return "9px";
  };

  const dynamicCallNumberStyle = {
    ...STYLES.callNumberMain,
    fontSize: getCallNumberFontSize(),
  };

  return (
    <>
      {/* Accession Number */}
      <div style={STYLES.accessionNumber}>
        <div style={STYLES.accessionValue}>{barcode}</div>
      </div>

      {/* Content Area */}
      <div style={STYLES.contentArea}>
        {/* Top Section - Call Number and Book Info */}
        <div style={STYLES.topSection}>
          {/* Left Column - Call Number */}
          <div style={STYLES.leftColumn}>
            <div style={STYLES.callNumberSection}>
              <div style={STYLES.callNumberValue}>
                {/* Call Number Prefix */}
                {callNumberPrefix && (
                  <div style={STYLES.callNumberPrefix}>{callNumberPrefix}</div>
                )}

                {/* Main Call Number Display with dynamic font size */}
                {baseCallNumber && baseCallNumber !== "N/A" ? (
                  <div style={dynamicCallNumberStyle}>
                    <div>{callNumberParts[0]}</div>
                    <div>{callNumberParts[1]}</div>
                    <div>{callNumberParts[2]}</div>
                  </div>
                ) : (
                  <div style={STYLES.callNumberMainPlaceholder}>
                    <div>&nbsp;</div>
                    <div>&nbsp;</div>
                    <div>&nbsp;</div>
                  </div>
                )}

                {/* Call Number Suffix */}
                {callNumberSuffix && (
                  <div style={STYLES.callNumberSuffix}>{callNumberSuffix}</div>
                )}
              </div>
            </div>
          </div>

          {/* Right Section - Title, Author */}
          <div style={STYLES.rightSection}>
            {/* Title */}
            <div style={STYLES.titleSection}>
              <div style={STYLES.titleValue}>{title}</div>
              <div style={STYLES.titleLabel}>(Book Title)</div>
            </div>

            {/* Author */}
            <div style={STYLES.authorSection}>
              <div style={STYLES.authorValue}>{renderAuthors()}</div>
              <div style={STYLES.authorLabel}>(Author)</div>
            </div>
          </div>
        </div>

        {/* Children (for loan table) */}
        {children}
      </div>
    </>
  );
};

BookCardField.propTypes = {
  title: PropTypes.string.isRequired,
  authors: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  hasOtherAuthors: PropTypes.bool,
  callNumber: PropTypes.string.isRequired,
  callNumberPrefix: PropTypes.string,
  baseCallNumber: PropTypes.string,
  callNumberSuffix: PropTypes.string,
  barcode: PropTypes.string.isRequired,
  formData: PropTypes.object,
  children: PropTypes.node,
};

export default BookCardField;
